import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import appStyle from '../../style.less'
import style from './style.less'
import Breadcrumbs from '@yaak/components/src/Breadcrumbs'
import Typography, {
  TypographySizes,
  TypographyTypes,
} from '@yaak/components/src/Typography/Typography'
import Divider from '@yaak/components/src/Divider'
import ProgressBar from '@yaak/components/src/ProgressBar'
import Button from '@yaak/components/src/Button'
import Switch from '@yaak/components/src/Switch'
import {
  getPartners,
  getSimMapConfig,
  updateSimMapConfig,
  Partner,
  SimMapConfigModule,
} from '@yaak/components/services/api/api'
import {
  ToastContext,
  ToastContextType,
} from '@yaak/components/context/toastContext'
import {
  formatGearModes,
  getEnabledModuleIds,
  groupModules,
  hasModuleConfigChanges,
} from '../../helpers/moduleConfig'

interface ModuleConfigPageProps {
  token: string
}

const ModuleConfigPage: React.FunctionComponent<ModuleConfigPageProps> = ({
  token,
}) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [partner, setPartner] = useState<Partner | null>(null)
  const [originalModules, setOriginalModules] = useState<SimMapConfigModule[]>(
    []
  )
  const [localModules, setLocalModules] = useState<SimMapConfigModule[]>([])

  const { partnerId } = useParams()
  const { setShowToast } = useContext(ToastContext) as ToastContextType

  useEffect(() => {
    const fetchData = async () => {
      if (!partnerId) return

      const [partners, mapConfig] = await Promise.all([
        getPartners({ token, onAlert: setShowToast, searchQuery: partnerId }),
        getSimMapConfig({ token, partnerId, onAlert: setShowToast }),
      ])

      const matchedPartner = partners?.find((p: Partner) => p.id === partnerId)
      setPartner(matchedPartner || null)

      const modules = mapConfig?.modules || []
      setOriginalModules(modules)
      setLocalModules(modules)
      setLoading(false)
    }

    token && fetchData()
  }, [token, partnerId])

  const toggleModule = (moduleId: string) => {
    setLocalModules((prev) =>
      prev.map((m) =>
        m.moduleId === moduleId ? { ...m, enabled: !m.enabled } : m
      )
    )
  }

  const hasChanges = hasModuleConfigChanges(localModules, originalModules)

  const save = async () => {
    if (!partnerId) return

    setSaving(true)
    const result = await updateSimMapConfig({
      token,
      partnerId,
      moduleIds: getEnabledModuleIds(localModules),
      onAlert: setShowToast,
    })

    if (result?.modules) {
      setOriginalModules(result.modules)
      setLocalModules(result.modules)
    }

    setSaving(false)
  }

  const grouped = useMemo(() => groupModules(localModules), [localModules])

  return (
    <div className={appStyle.page}>
      <div className={style.container}>
        <Breadcrumbs
          first={{
            text: 'Partner Modules',
            url: '/simulator/module-config',
          }}
          second={{
            text: partner?.name,
          }}
          selfLink={true}
        />
        <div className={style.header}>
          <Typography
            type={TypographyTypes.headline}
            size={TypographySizes.small}
          >
            {partner?.name || 'Loading...'}
          </Typography>
          <Button
            onClick={save}
            text={saving ? 'Saving...' : 'Save'}
            disabled={!hasChanges || saving}
          />
        </div>
        <Divider />
      </div>
      {loading ? (
        <ProgressBar />
      ) : localModules.length === 0 ? (
        <div className={style.emptyState}>
          <Typography type={TypographyTypes.body}>
            {'No modules found for this partner.'}
          </Typography>
        </div>
      ) : (
        <div className={style.modulesContainer}>
          <table className={style.moduleTable}>
            <thead>
              <tr>
                <th className={style.thIndex}>#</th>
                <th className={style.thName}>Module</th>
                <th className={style.thGear}>Gear modes</th>
                <th className={style.thVersions}>Supported versions</th>
                <th className={style.thDevices}>Devices</th>
                <th className={style.thEnabled}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((group) => (
                <React.Fragment key={group.groupName}>
                  <tr className={style.groupRow}>
                    <td colSpan={6} className={style.groupCell}>
                      <Typography type={TypographyTypes.title}>
                        {group.groupName}
                      </Typography>
                    </td>
                  </tr>
                  {group.modules.map((module) => (
                    <tr key={module.moduleId}>
                      <td className={style.tdIndex}>{module.displayIndex}</td>
                      <td className={style.tdName}>{module.title}</td>
                      <td className={style.tdGear}>
                        {formatGearModes(module.gearModes)}
                      </td>
                      <td className={style.tdVersions}>
                        {module.supportedVersions?.length
                          ? module.supportedVersions.join(', ')
                          : '-'}
                      </td>
                      <td
                        className={
                          module.partnerDeviceCount === 0
                            ? style.tdDevicesWarning
                            : style.tdDevices
                        }
                        title={
                          module.partnerDeviceCount === 0
                            ? 'No devices at this partner can run this module yet.'
                            : undefined
                        }
                      >
                        {module.partnerDeviceCount}
                      </td>
                      <td className={style.tdEnabled}>
                        <Switch
                          checked={module.enabled}
                          onChange={() => toggleModule(module.moduleId)}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default ModuleConfigPage
